/**
 * Browser-side logging with levels, timestamps, and a level you can change from the console.
 *
 * The counterpart to `backend/core/logging_setup.py`. Same idea, same level names, same JSON shape
 * where it can be — so a frontend line and a backend line describing the same request can sit in
 * the same index and be correlated by `request_id`.
 *
 * ## Changing the level at runtime
 *
 * In the browser console, on any page of the app:
 *
 *     dtLog.setLevel("debug")     // then reproduce the problem
 *     dtLog.getLevel()
 *     dtLog.setFormat("json")     // structured output, for copying into a ticket
 *     dtLog.reset()               // back to the environment default
 *
 * The choice is kept in `localStorage`, so it survives a reload and a navigation — which is the
 * point: a bug that needs DEBUG usually needs it across the reload that reproduces it. It is
 * per-browser and per-origin, so turning it on affects only the person who typed it.
 *
 * ## Why not just use console.log
 *
 * `console.log` cannot be turned down. Left in, it is noise in every user's console forever;
 * removed, it is not there on the day you need it. A level is what lets the same statement be
 * silent in production and present in an incident, which is the whole reason this file exists.
 */

const LEVELS = { debug: 10, info: 20, warn: 30, error: 40, silent: 99 };

const LEVEL_KEY = "dt_log_level";
const FORMAT_KEY = "dt_log_format";

const SERVICE = "dharmatn-frontend";

/** `localStorage` throws outright in some privacy modes, so every access is guarded. */
function readStored(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key, value) {
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — the setting just does not persist across reloads */
  }
}

/**
 * The level this session starts at.
 *
 * `REACT_APP_LOG_LEVEL` is the build-time default; absent that it follows the build, because the
 * two audiences differ. In development the person reading the console wrote the code and wants
 * `debug`. In production the console belongs to a customer, so only `warn` and `error` — things
 * that are actually wrong — are worth their attention, and `debug` would leak the shape of every
 * API response into a place we do not control.
 */
function envDefaultLevel() {
  const configured = (process.env.REACT_APP_LOG_LEVEL || "").trim().toLowerCase();
  if (configured && configured in LEVELS) return configured;
  return process.env.NODE_ENV === "production" ? "warn" : "debug";
}

let currentLevel = (() => {
  const stored = (readStored(LEVEL_KEY) || "").trim().toLowerCase();
  return stored && stored in LEVELS ? stored : envDefaultLevel();
})();

let currentFormat = readStored(FORMAT_KEY) === "json" ? "json" : "text";

function enabled(level) {
  return LEVELS[level] >= LEVELS[currentLevel];
}

// console.debug is hidden behind "Verbose" in Chrome's default filter, which makes a debug line
// look like it never happened. Routed to console.log instead, where it is actually visible.
const SINKS = { debug: "log", info: "info", warn: "warn", error: "error" };

function emit(level, name, args) {
  if (!enabled(level)) return;
  const ts = new Date().toISOString();
  const sink = console[SINKS[level]] || console.log;

  // An Error anywhere in the arguments is passed through untouched in text mode, so the browser
  // still renders it as an expandable stack rather than as "[object Object]".
  if (currentFormat === "json") {
    const parts = [];
    const fields = {};
    for (const a of args) {
      if (a instanceof Error) {
        fields.error = { type: a.name, message: a.message, stack: a.stack };
      } else if (a && typeof a === "object" && !Array.isArray(a)) {
        Object.assign(fields, a);
      } else {
        parts.push(String(a));
      }
    }
    sink.call(
      console,
      JSON.stringify({
        ts,
        level: level.toUpperCase(),
        logger: name,
        msg: parts.join(" "),
        service: SERVICE,
        env: process.env.NODE_ENV,
        url: typeof window !== "undefined" ? window.location.pathname : undefined,
        ...fields,
      }),
    );
    return;
  }

  sink.call(console, `${ts.slice(11, 23)} ${level.toUpperCase().padEnd(5)} ${name}`, ...args);
}

/** A logger bound to a name — `logger.child("apiClient")` prefixes every line with it. */
function makeLogger(name) {
  return {
    name,
    debug: (...a) => emit("debug", name, a),
    info: (...a) => emit("info", name, a),
    warn: (...a) => emit("warn", name, a),
    error: (...a) => emit("error", name, a),
    child: (childName) => makeLogger(`${name}.${childName}`),
  };
}

export const logger = makeLogger("dharmatn");

export function setLevel(level) {
  const next = String(level || "").trim().toLowerCase();
  if (!(next in LEVELS)) {
    console.warn(`Unknown log level "${level}". One of: ${Object.keys(LEVELS).join(", ")}`);
    return currentLevel;
  }
  currentLevel = next;
  writeStored(LEVEL_KEY, next);
  console.info(`[dharmatn] log level is now ${next} (persisted for this browser)`);
  return currentLevel;
}

export function getLevel() {
  return currentLevel;
}

export function setFormat(format) {
  const next = String(format || "").trim().toLowerCase() === "json" ? "json" : "text";
  currentFormat = next;
  writeStored(FORMAT_KEY, next);
  console.info(`[dharmatn] log format is now ${next}`);
  return currentFormat;
}

export function getFormat() {
  return currentFormat;
}

/** Drop the stored override and go back to whatever the build defaults to. */
export function reset() {
  writeStored(LEVEL_KEY, null);
  writeStored(FORMAT_KEY, null);
  currentLevel = envDefaultLevel();
  currentFormat = "text";
  console.info(`[dharmatn] log settings reset — level is ${currentLevel}`);
  return currentLevel;
}

// Exposed on `window` deliberately, in every build including production. This is the runtime knob:
// it has to be reachable by someone who is on the phone to a customer, without a rebuild, a deploy
// or a devtools breakpoint. It only affects that one browser, and it reads and writes nothing but
// the two localStorage keys above.
if (typeof window !== "undefined") {
  window.dtLog = {
    setLevel,
    getLevel,
    setFormat,
    getFormat,
    reset,
    levels: Object.keys(LEVELS),
    help: "dtLog.setLevel('debug'|'info'|'warn'|'error'|'silent'); dtLog.setFormat('json'|'text'); dtLog.reset()",
  };
}

export default logger;
